import { customExtensionsUrl } from '../../api/console-client'
import type { DeployResult, PlannedService, Selection, ServiceStatus } from '../../operations/deploy'
import { deploy } from '../../operations/deploy'
import { describeWorkspace } from '../../project/auth'
import { plural } from '../../utils/format'
import { fail } from '../fail'
import { interactiveLogin } from '../interactive-login'
import { say, section } from '../output'
import { canRenderList, selectFromList } from '../prompts/select-list'
import { reportDeploy } from '../reporter'

export interface DeployCommandOptions {
  /** Repeatable `-s`. */
  service?: string[]
  all?: boolean
  force?: boolean
}

export async function deployCommand(options: DeployCommandOptions = {}): Promise<void> {
  // The blank line that follows belongs to whatever comes next — `section` puts it there, so this reads
  // the same whether the next thing is a service list, a login, or a failure.
  say('Deploying custom extensions...')

  try {
    const result = await deploy({
      serviceIds: options.service,
      all: options.all,
      force: options.force,
      onEvent: reportDeploy,
      onAuthRequired: interactiveLogin,
      // Only offered when there is a terminal to draw it on. Without one the operation throws
      // SelectionRequiredError, and `fail` prints the two flags that answer it.
      onSelectionRequired: canRenderList() ? pickServices : undefined,
    })

    const where = describeWorkspace(result.workspace)
    const them = result.services.length > 1 ? 'them' : 'it'

    if (result.services.length === 0) {
      section(`  Nothing to deploy — workspace ${ where } is already up to date.`)
      sayConsoleLink(result, them)

      return
    }

    section(`  Deployed ${ plural(result.services.length, 'service') } to workspace ${ where }` +
      ` — you can use ${ them } in a flow.`)
    sayConsoleLink(result, them)
  } catch (error) {
    fail(error)
  }
}

/**
 * Where to go next, as a link rather than an instruction to go and find it.
 *
 * Falls back to naming the page when the project has no workspace name recorded: the console's route is
 * `/app/:appName/…`, so without one there is no URL to offer.
 */
function sayConsoleLink(result: DeployResult, them: string): void {
  const { workspaceName } = result.workspace
  const page = `  Open Custom Extensions screen in the Flowrunner Console to configure ${ them }`

  // Without a name there is nothing to put in the console's `/app/:appName/…` slot, and a link to
  // `/app//custom-extensions` is worse than no link. The sentence stands on its own either way.
  say(workspaceName
    ? `${ page }: ${ customExtensionsUrl(result.serverUrl, workspaceName) }`
    : `${ page }.`)

  say('')
}

/** What deploying each one would do, so the list answers "which of these needs deploying?". */
const STATUS_SUFFIX: Record<ServiceStatus, string> = {
  new: ' — new',
  modified: ' — modified',
  same: ' — unchanged',
}

/**
 * Ask which services to deploy.
 *
 * A single-choice list with an "every service" entry at the top, rather than a multi-select widget: `-s` is
 * already the precise answer, and this only has to cover "all of them" and "just that one".
 *
 * One line per option. A second, dimmed line per entry doubled the height of the list and pushed the
 * services themselves apart, which is the one thing the list is for reading.
 */
async function pickServices(available: PlannedService[]): Promise<Selection> {
  const choices = [
    { label: `All services [${ available.map(entry => entry.serviceId).join(', ') }]` },
    ...available.map(entry => ({
      label: `${ entry.serviceId } (${ entry.service.serviceInfo.integrationName })${ STATUS_SUFFIX[entry.status] }`,
    })),
  ]

  const index = await selectFromList('Which services should be deployed?', choices)

  return index === 0
    ? { all: true }
    : { serviceIds: [available[index - 1].serviceId] }
}
